/**
 * Which posted, non-hourly rate a listing card should show.
 *
 * The backend dual-populates derived figures so a per-week, per-day or
 * per-shift posting still reaches providers who filter on an hourly rate or a
 * salary. Those derived numbers are for MATCHING; showing one as though the
 * poster wrote it is what made a "$2,200/day" role read as a posted "$275/hr".
 *
 * `estimatedFromPeriod` names the unit the poster actually used and
 * `postedAmount`/`postedMaxAmount` carry the verbatim figures. Several card
 * surfaces implemented this rule for `'weekly'` only, so per-day and per-shift
 * postings fell through to the derived hourly and were presented as posted.
 * This centralises the decision; each surface keeps its own formatting.
 *
 * Returns `null` when there is no posted non-hourly figure to show, which
 * includes legacy derived rows that kept no recoverable `postedAmount` -- there
 * is nothing honest to render, so the caller falls back to its own display.
 */

/** estimatedFromPeriod -> the noun a card should print. */
const PERIOD_UNIT = { weekly: 'week', daily: 'day', shift: 'shift' };

export const getPostedPeriodRate = (compensation) => {
  const comp = compensation || {};
  const unit = PERIOD_UNIT[comp.estimatedFromPeriod];

  if (unit && comp.postedAmount > 0) {
    return {
      amount: comp.postedAmount,
      maxAmount: comp.postedMaxAmount > comp.postedAmount ? comp.postedMaxAmount : 0,
      unit,
    };
  }

  // Fallback for the matcher/gaswork extractor path, which stores a bare
  // weeklyAmount with no estimatedFromPeriod stamp.
  if (comp.weeklyAmount > 0) {
    return { amount: comp.weeklyAmount, maxAmount: 0, unit: 'week' };
  }

  return null;
};

/**
 * The posted period rate as a display string, or null when there is none.
 *
 * Weekly keeps the `$10.2K/week` idiom the card surfaces already used, so this
 * change is invisible for weekly rows. Per-day and per-shift figures are small
 * enough that thousands-notation reads badly ("$2.2K/day"), so they render in
 * whole dollars: "$2,200/day".
 */
export const formatPostedPeriodRate = (compensation) => {
  const posted = getPostedPeriodRate(compensation);
  if (!posted) return null;
  const { amount, maxAmount, unit } = posted;

  if (unit === 'week') {
    const wk = (v) =>
      Number.isInteger(v / 1000) ? `${v / 1000}` : (v / 1000).toFixed(1);
    return maxAmount > amount
      ? `$${wk(amount)}-${wk(maxAmount)}K/week`
      : `$${wk(amount)}K/week`;
  }

  const dollars = (v) => Math.round(v).toLocaleString('en-US');
  return maxAmount > amount
    ? `$${dollars(amount)}-${dollars(maxAmount)}/${unit}`
    : `$${dollars(amount)}/${unit}`;
};

export default getPostedPeriodRate;
